import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { listAppInstances } from '@/features/apps/api';
import { presentAppPickerLabel } from '@/features/apps/leftoverDevHosting';
import { useAppStore } from '@/store/appStore';
import { useAuthStore } from '@/store/authStore';

export function AppSwitcher() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { appId, setApp } = useAppStore();
  const selectedTenant = useAuthStore((s) => s.selectedTenant);

  const q = useQuery({
    queryKey: ['app-instances', 'switcher-list'],
    queryFn: listAppInstances,
    staleTime: 10_000,
  });

  const apps = Array.isArray(q.data?.apps) ? q.data!.apps : [];
  const selected = appId || '';

  const resolved = useMemo(() => {
    if (!selected) return null;
    return apps.find((a) => a.app_id === selected) || null;
  }, [apps, selected]);

  const handleChange = (nextAppId: string) => {
    if (!nextAppId || nextAppId === selected) return;
    const a = apps.find((x) => x.app_id === nextAppId) || null;
    setApp({ appId: nextAppId, appName: a?.name || null });
    // Force a full refresh so all views re-resolve app-scoped data.
    window.location.reload();
  };

  const goManage = () => {
    if (!loc.pathname.startsWith('/apps')) navigate('/apps');
  };

  return (
    <div className="flex items-center gap-2">
      <label className="text-sm text-gray-600">App:</label>
      <select
        value={selected}
        onChange={(e) => handleChange(e.target.value)}
        className="text-sm border border-gray-300 rounded px-2 py-1 bg-white max-w-[260px]"
      >
        <option value="" disabled>
          {q.isLoading ? 'Loading…' : 'Select app…'}
        </option>
        {apps.map((a) => (
          <option key={a.app_id} value={a.app_id}>
            {presentAppPickerLabel({
              name: a.name,
              app_id: a.app_id,
              status: a.status,
              tenantId: selectedTenant,
              picker_honesty: a.picker_honesty,
              picker_honesty_label: a.picker_honesty_label,
              hosting_url: a.hosting_url,
              hosting_leftover_url: a.hosting_leftover_url,
              hosting_honesty: a.hosting_honesty,
              hosting_honesty_label: a.hosting_honesty_label,
            })}
          </option>
        ))}
      </select>
      <button
        onClick={goManage}
        className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
        title="Manage app instances"
      >
        Manage
      </button>
      {resolved?.status && (
        <span className="text-xs text-gray-500">
          {String(resolved.status)}
        </span>
      )}
    </div>
  );
}

